import React from 'react';
import { StreamStatusPill } from '@experience/shared-ui';
import { ExperienceIcon } from '../icons';
import type {
  ContributionNotification,
  ContributionView,
} from '../../hooks/useContributions';

export interface NeedsYouRailProps {
  view: ContributionView | null;
  loading: boolean;
  connectionState: string;
  dataStale: boolean;
  /** Navigate to a validated same-origin in-app path (action.url). */
  onNavigate: (url: string) => void;
  /** Overflow "+N more" -> full notifications view. Also used as the empty-state CTA. */
  onSeeAll?: () => void;
  /** Manual reconnect for the status pill when stale. */
  onRetry?: () => void;
}

interface SeverityConfig {
  icon: string;
  color: string;
  label: string;
}

// Status-honest: red/amber genuinely map to critical/warning here; violet is the
// neutral "informational" accent (not a warning color). See honest-status invariant.
const SEVERITY: Record<string, SeverityConfig> = {
  critical: {
    icon: 'shield-alert',
    color: 'var(--color-danger, var(--error-color, #d4380d))',
    label: 'Critical',
  },
  warning: {
    icon: 'alert-triangle',
    color: 'var(--warning-color, #d48806)',
    label: 'Warning',
  },
  info: {
    icon: 'message-square',
    color: 'var(--main-accent, var(--quantum-violet, #6C4CFF))',
    label: 'Info',
  },
};

function severityOf(n: ContributionNotification): SeverityConfig {
  return SEVERITY[n.severity] || SEVERITY.info;
}

function messageText(n: ContributionNotification): string {
  return n.message?.fallback || n.message?.messageKey || 'Attention needed';
}

/** Human-friendly relative time from a unix timestamp (seconds or ms; tolerant of floats). */
function relativeTime(ts: number | undefined | null): string {
  if (!ts || !Number.isFinite(ts)) return '';
  const ms = ts < 1e12 ? ts * 1000 : ts;
  const diff = Date.now() - ms;
  if (diff < 45_000) return 'just now';
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  try {
    return new Date(ms).toLocaleDateString();
  } catch {
    return '';
  }
}

/** Client-side re-validation of the action target (defense in depth; ingest also validates). */
function safeInAppPath(url: string | undefined | null): string | null {
  if (!url || typeof url !== 'string') return null;
  if (!url.startsWith('/') || url.startsWith('//')) return null;
  if (url.includes('\\')) return null;
  return url;
}

function ChevronRight() {
  return (
    <svg
      className="needsyou-item__chev-icon"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9 18l6-6-6-6" />
    </svg>
  );
}

function NeedsYouItem({
  n,
  index,
  onNavigate,
}: {
  n: ContributionNotification;
  index: number;
  onNavigate: (url: string) => void;
}) {
  const href = safeInAppPath(n.action?.url);
  const sev = severityOf(n);
  const text = messageText(n);
  const when = relativeTime(n.updatedAt);
  const label = `${sev.label}: ${text}`;

  const activate = () => {
    if (href) onNavigate(href);
  };
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (href && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      activate();
    }
  };

  return (
    <li
      className="needsyou-item"
      data-testid={`needsyou-item-${n.itemId}`}
      data-severity={n.severity}
      role={href ? 'button' : undefined}
      tabIndex={href ? 0 : undefined}
      aria-label={href ? `${label} — open` : label}
      onClick={href ? activate : undefined}
      onKeyDown={href ? onKeyDown : undefined}
      style={{ ['--ny-accent' as any]: sev.color, ['--ny-i' as any]: index }}
    >
      <span className="needsyou-item__sev" data-severity={n.severity} aria-hidden="true">
        <ExperienceIcon iconKey={sev.icon} size={16} />
      </span>
      <span className="needsyou-item__body">
        <span className="needsyou-item__text" title={text}>
          {text}
        </span>
        {when && <span className="needsyou-item__meta">{when}</span>}
      </span>
      {href && (
        <span className="needsyou-item__chev" aria-hidden="true">
          <ChevronRight />
        </span>
      )}
    </li>
  );
}

export function NeedsYouRail({
  view,
  loading,
  connectionState,
  dataStale,
  onNavigate,
  onSeeAll,
  onRetry,
}: NeedsYouRailProps) {
  const items = view?.needsYou.items ?? [];
  const overflow = view?.needsYou.overflow ?? 0;
  const hasItems = items.length > 0;
  // Okta-level polish: when nothing needs the user, the rail minimizes to a
  // single slim "all caught up" strip rather than a tall empty-state block.
  const minimized = !loading && !hasItems;

  return (
    <section
      className={`needsyou-rail${minimized ? ' needsyou-rail--minimized' : ''}`}
      data-testid="needsyou-rail"
      data-state={minimized ? 'empty' : 'active'}
      aria-label="Needs you"
    >
      <div className="needsyou-rail__head">
        <div className="needsyou-rail__head-left">
          <span className="needsyou-rail__head-icon" aria-hidden="true">
            <ExperienceIcon iconKey="inbox" size={16} />
          </span>
          <h2 className="needsyou-rail__title">Needs you</h2>
          {!loading && hasItems && (
            <span className="needsyou-rail__count" data-testid="needsyou-count">
              {items.length}
              {overflow > 0 ? '+' : ''}
            </span>
          )}
          {minimized && (
            <span
              className="needsyou-rail__clear"
              data-testid="needsyou-empty"
              role="status"
            >
              <ExperienceIcon iconKey="check-circle" size={13} />
              All caught up
            </span>
          )}
        </div>
        <StreamStatusPill
          connectionState={connectionState as any}
          dataStale={dataStale}
          onRetry={onRetry}
        />
      </div>

      {loading ? (
        <ul className="needsyou-rail__list" data-testid="needsyou-loading" aria-hidden="true">
          {[0, 1, 2].map((i) => (
            <li key={i} className="needsyou-item needsyou-item--skeleton">
              <span className="needsyou-item__sev needsyou-item__sev--skeleton" />
              <span className="needsyou-item__body">
                <span className="needsyou-item__text needsyou-item__text--skeleton" />
              </span>
            </li>
          ))}
        </ul>
      ) : !hasItems ? null : (
        <>
          <ul
            className="needsyou-rail__list"
            aria-live="polite"
            aria-relevant="additions text"
          >
            {items.map((n, i) => (
              <NeedsYouItem key={n.itemId} n={n} index={i} onNavigate={onNavigate} />
            ))}
          </ul>
          {overflow > 0 && (
            <button
              type="button"
              className="needsyou-rail__overflow"
              data-testid="needsyou-overflow"
              onClick={onSeeAll}
              aria-label={`Show ${overflow} more notifications`}
            >
              <ExperienceIcon iconKey="layers" size={14} />
              {`+${overflow} more`}
            </button>
          )}
        </>
      )}

      <style>{`
        .needsyou-rail {
          background: var(--surface-1, var(--card-bg-solid, var(--card-bg)));
          border: 1px solid var(--card-border, var(--border-color, rgba(120,130,150,0.28)));
          border-radius: var(--card-radius, 12px);
          padding: 14px 16px 16px;
        }
        .needsyou-rail__head {
          display: flex; align-items: center; justify-content: space-between;
          gap: 8px; margin-bottom: 12px;
        }
        .needsyou-rail__head-left { display: flex; align-items: center; gap: 8px; min-width: 0; }
        .needsyou-rail__head-icon {
          display: inline-flex; align-items: center; justify-content: center;
          color: var(--main-text-secondary, #94a3b8);
        }
        .needsyou-rail__title {
          margin: 0; font-size: 14px; font-weight: 600;
          color: var(--section-title-color, var(--main-text));
          letter-spacing: -0.01em;
        }
        .needsyou-rail__count {
          display: inline-flex; align-items: center; justify-content: center;
          min-width: 20px; height: 20px; padding: 0 6px;
          font-size: 11px; font-weight: 700; line-height: 1;
          color: var(--color-on-accent, #ffffff);
          background: var(--main-accent, var(--quantum-violet, #6C4CFF));
          border-radius: 999px;
        }
        .needsyou-rail__list { list-style: none; margin: 0; padding: 0; display: grid; gap: 6px; }

        .needsyou-item {
          position: relative;
          display: flex; align-items: center; gap: 10px;
          min-height: 48px; padding: 8px 10px 8px 12px;
          border-radius: 10px;
          border: 1px solid transparent;
          color: var(--main-text);
          overflow: hidden;
          transition: background var(--dur-fast, 150ms) var(--ease-standard, ease),
                      border-color var(--dur-fast, 150ms) var(--ease-standard, ease),
                      transform var(--dur-fast, 150ms) var(--ease-standard, ease);
          animation: needsyou-in var(--dur-base, 240ms) var(--ease-standard, ease) both;
          animation-delay: calc(min(var(--ny-i, 0), 6) * 45ms);
        }
        /* Severity accent bar down the left edge */
        .needsyou-item::before {
          content: ''; position: absolute; left: 0; top: 8px; bottom: 8px; width: 3px;
          border-radius: 0 3px 3px 0;
          background: var(--ny-accent, var(--main-accent, #6C4CFF));
          opacity: 0.85;
        }
        .needsyou-item[role="button"] { cursor: pointer; }
        .needsyou-item[role="button"]:hover {
          background: color-mix(in srgb, var(--ny-accent, var(--main-accent, #6C4CFF)) 9%, transparent);
          border-color: color-mix(in srgb, var(--ny-accent, var(--main-accent, #6C4CFF)) 28%, transparent);
          transform: translateY(-1px);
        }
        .needsyou-item[role="button"]:active { transform: translateY(0); }
        .needsyou-item[role="button"]:focus-visible {
          outline: 2px solid var(--focus-ring, var(--main-accent, #6C4CFF));
          outline-offset: 2px;
        }

        .needsyou-item__sev {
          display: inline-flex; align-items: center; justify-content: center;
          width: 30px; height: 30px; flex-shrink: 0; border-radius: 8px;
          color: var(--ny-accent, var(--main-accent, #6C4CFF));
          background: color-mix(in srgb, var(--ny-accent, var(--main-accent, #6C4CFF)) 15%, transparent);
        }
        .needsyou-item__body { flex: 1; min-width: 0; display: grid; gap: 1px; }
        .needsyou-item__text {
          min-width: 0; font-size: 13px; line-height: 1.35; font-weight: 500;
          color: var(--main-text);
          overflow: hidden; text-overflow: ellipsis;
          display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
        }
        .needsyou-item__meta {
          font-size: 11px; line-height: 1.2;
          color: var(--main-text-secondary, #94a3b8);
        }
        .needsyou-item__chev {
          display: inline-flex; align-items: center; flex-shrink: 0;
          color: var(--main-text-secondary, #94a3b8);
          opacity: 0.6;
          transition: transform var(--dur-fast, 150ms) var(--ease-standard, ease),
                      opacity var(--dur-fast, 150ms) var(--ease-standard, ease);
        }
        .needsyou-item[role="button"]:hover .needsyou-item__chev {
          opacity: 1; transform: translateX(2px); color: var(--ny-accent, var(--main-accent, #6C4CFF));
        }

        /* Minimized (empty) state — collapse to a single slim, reassuring strip. */
        .needsyou-rail--minimized {
          padding: 12px 16px;
          transition: padding var(--dur-fast, 150ms) var(--ease-standard, ease);
        }
        .needsyou-rail--minimized .needsyou-rail__head { margin-bottom: 0; }
        .needsyou-rail__clear {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 2px 9px; border-radius: 999px; line-height: 1;
          font-size: 11.5px; font-weight: 600; white-space: nowrap;
          color: var(--color-success, var(--success-color, #16a34a));
          background: color-mix(in srgb, var(--color-success, var(--success-color, #16a34a)) 12%, transparent);
          animation: needsyou-in var(--dur-base, 240ms) var(--ease-standard, ease) both;
        }

        /* Overflow affordance */
        .needsyou-rail__overflow {
          display: flex; align-items: center; justify-content: center; gap: 6px;
          margin-top: 8px; padding: 8px 10px; min-height: 40px; width: 100%;
          background: none;
          border: 1px dashed var(--card-border, var(--border-color, rgba(120,130,150,0.28)));
          border-radius: 10px;
          color: var(--main-accent, var(--quantum-violet, #6C4CFF));
          font-size: 12px; font-weight: 600; cursor: pointer; font-family: inherit;
          transition: background var(--dur-fast, 150ms) var(--ease-standard, ease),
                      border-color var(--dur-fast, 150ms) var(--ease-standard, ease);
        }
        .needsyou-rail__overflow:hover {
          background: color-mix(in srgb, var(--main-accent, #6C4CFF) 8%, transparent);
          border-color: color-mix(in srgb, var(--main-accent, #6C4CFF) 40%, transparent);
        }
        .needsyou-rail__overflow:focus-visible {
          outline: 2px solid var(--focus-ring, var(--main-accent, #6C4CFF)); outline-offset: 2px;
        }

        /* Skeleton */
        .needsyou-item--skeleton { pointer-events: none; animation: none; }
        .needsyou-item--skeleton::before { display: none; }
        .needsyou-item__sev--skeleton {
          width: 30px; height: 30px; border-radius: 8px;
          background: color-mix(in srgb, var(--main-text-secondary, #94a3b8) 18%, transparent);
          animation: needsyou-shimmer 1.4s ease-in-out infinite;
        }
        .needsyou-item__text--skeleton {
          height: 12px; border-radius: 4px; width: 62%;
          background: color-mix(in srgb, var(--main-text-secondary, #94a3b8) 18%, transparent);
          animation: needsyou-shimmer 1.4s ease-in-out infinite;
        }

        @keyframes needsyou-in {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes needsyou-shimmer {
          0%, 100% { opacity: 0.55; }
          50% { opacity: 0.9; }
        }
        @media (prefers-reduced-motion: reduce) {
          .needsyou-item,
          .needsyou-rail__clear { animation: none; }
          .needsyou-item[role="button"]:hover { transform: none; }
          .needsyou-item__chev { transition: none; }
          .needsyou-item__sev--skeleton,
          .needsyou-item__text--skeleton { animation: none; }
          .needsyou-item[role="button"]:hover { transform: none; }
        }
      `}</style>
    </section>
  );
}

export default NeedsYouRail;
